import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BookOpenCheck, ArrowRight, ChevronRight, Layers, Target } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { Logo } from "@/components/Logo";
import { PatternBadge, TYPE_LABEL } from "./shared";

export default function SubjectLanding() {
  const { slug, subject } = useParams();
  const [d, setD] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    setD(null); setMissing(false);
    api.get(`/test-prep/public/exams/${slug}/${subject}`).then((r) => setD(r.data)).catch(() => setMissing(true));
  }, [slug, subject]);

  if (missing) return <Shell><p className="text-slate-400">This subject page is not available.</p></Shell>;
  if (!d) return <Shell><p className="text-slate-400">Loading...</p></Shell>;
  const { exam, subject: subj, chapters, tests, other_subjects, has_content, seo } = d;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: `${exam.name} ${subj.name} Mock Test`,
    description: seo.description,
    provider: { "@type": "Organization", name: "EntranceZone AI", sameAs: "https://ai.entrancezone.com" },
    about: `${exam.name} ${subj.name}`,
  };
  const breadcrumbLd = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: `${exam.name} Mock Test`, item: `https://ai.entrancezone.com/mock-test/${exam.slug}` },
      { "@type": "ListItem", position: 2, name: `${subj.name}`, item: `https://ai.entrancezone.com${seo.canonical}` },
    ],
  };

  return (
    <Shell>
      <Seo title={`${exam.name} ${subj.name} Mock Test ${exam.session}`} description={seo.description} path={seo.canonical} jsonLd={[jsonLd, breadcrumbLd]} />

      <nav data-testid="subject-breadcrumb" className="mb-5 flex flex-wrap items-center gap-1 text-xs text-slate-500">
        <Link to="/" className="hover:text-cyan-300">Home</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to={`/mock-test/${exam.slug}`} className="hover:text-cyan-300">{exam.name} Mock Test</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-slate-300">{subj.name}</span>
      </nav>

      <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-cyan-400">{exam.name} · Session {exam.session}</p>
      <h1 data-testid="subject-landing-title" className="mt-2 text-4xl font-extrabold tracking-tight sm:text-5xl">
        {exam.name} {subj.name} Mock Test {exam.session}
      </h1>
      <p className="mt-3 max-w-2xl text-slate-400">
        Chapter-wise and subject-wise {subj.name} practice for {exam.name} with deterministic scoring, weak-area detection and a
        direct path into EntranceZone AI Rank &amp; College Prediction.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <PatternBadge status={exam.pattern_status} />
        <span data-testid="subject-question-count" className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-slate-300">
          <Layers className="h-3.5 w-3.5 text-cyan-300" /> {subj.question_count} live practice question{subj.question_count !== 1 ? "s" : ""}
        </span>
      </div>

      <Link to="/auth" data-testid="subject-landing-cta" className="mt-6 inline-flex items-center gap-2 rounded-full bg-cyan-400 px-7 py-3 text-sm font-bold text-[#041016] transition-transform hover:scale-105">
        <BookOpenCheck className="h-4 w-4" /> Start {subj.name} practice <ArrowRight className="h-4 w-4" />
      </Link>

      {!has_content && (
        <div data-testid="subject-coming-soon" className="mt-8 rounded-2xl border border-amber-400/20 bg-amber-400/5 px-5 py-4 text-sm text-amber-200">
          Verified {subj.name} practice content for {exam.name} is coming soon. Create a free account to be ready the moment it goes live.
        </div>
      )}

      {tests.length > 0 && (
        <>
          <h2 className="mt-12 text-base font-bold md:text-lg">Available {subj.name} tests</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {tests.map((t, i) => (
              <div key={i} data-testid="subject-available-test" className="rounded-2xl border border-white/8 bg-[#131C2E] p-4">
                <p className="font-mono-data text-[11px] uppercase tracking-wider text-cyan-400">{TYPE_LABEL[t.type] || t.type}</p>
                <p className="mt-1 font-semibold">{t.title}</p>
                <p className="mt-1 text-xs text-slate-400">{t.question_count} questions</p>
              </div>
            ))}
          </div>
        </>
      )}

      {chapters.length > 0 && (
        <>
          <h2 className="mt-12 text-base font-bold md:text-lg">{subj.name} chapters &amp; topics</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {chapters.map((c) => (
              <div key={c.id} data-testid="subject-chapter" className="rounded-2xl border border-white/8 bg-[#131C2E] p-4">
                <div className="flex items-center justify-between">
                  <p className="font-semibold">{c.name}</p>
                  <span className="text-xs text-slate-500">{c.question_count} Q</span>
                </div>
                {c.topics.length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs text-slate-400">
                    {c.topics.map((t) => (
                      <li key={t.id} className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-1"><Target className="h-3 w-3 text-slate-600" /> {t.name}</span>
                        <span className="text-slate-600">{t.question_count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {other_subjects.length > 0 && (
        <>
          <h2 className="mt-12 text-base font-bold md:text-lg">More {exam.name} subject mock tests</h2>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {other_subjects.map((o) => (
              <li key={o.slug}>
                <Link to={`/mock-test/${exam.slug}/${o.slug}`} data-testid={`other-subject-${o.slug}`}
                  className="flex items-center justify-between rounded-xl border border-white/8 bg-[#131C2E] px-4 py-3 text-sm transition-colors hover:border-cyan-400/40">
                  <span className="font-medium">{exam.name} {o.name} Mock Test</span>
                  <span className="text-xs text-slate-500">{o.question_count} Q</span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="mt-10 text-xs text-slate-600">
        Practice questions on this platform are clearly labelled by source. Sample practice content is not official or previous-year
        exam content. Rank/percentile outputs are estimates, never official results.
      </p>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#0B0F17] text-white">
    <header className="border-b border-white/8 px-6 py-4"><Logo /></header>
    <main className="mx-auto max-w-5xl px-6 py-12">{children}</main>
  </div>
);
